import React, { useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CaretLeftIcon } from '@/atoms/Icons/CaretLeftIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { LabelMD } from '@/atoms/Text'
import { SocialSharing } from '@/organisms/SocialSharing'
import { useNativeMobileShare } from '@/services/Sharing'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'

interface ConfirmationStepProps {
  handleGoBack: () => void
  onClose: () => void
  handleGiftConfirm: () => Promise<void>
  ticketQuantity: number
  projectName: string
  isLoading: boolean
  shareUrl: string
}

export const ShareLinkStep = ({
  handleGoBack,
  onClose,
  ticketQuantity,
  projectName,
  shareUrl,
}: ConfirmationStepProps) => {
  const { t } = useTranslate('common')
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
  const { share } = useNativeMobileShare()

  const title = t('shareMyTickets', 'Share My Tickets')

  const emailContent = React.useMemo(
    () => ({
      subject: t(
        'giftedTicketsSubject',
        `You've been gifted ({{ numberOfCodes }}) Premium Guild tickets to {{ projectName }}.`,
        {
          numberOfCodes: ticketQuantity,
          projectName: projectName,
        },
      ),
      body:
        t('giftedTicketsBody', `Use the link below to claim your free tickets and enjoy the show!`) + '\n\n' + shareUrl,
    }),
    [t, ticketQuantity, projectName, shareUrl],
  )

  useEffect(() => {
    if (isMobile && share) {
      share({
        title: emailContent.subject,
        text: emailContent.body,
        url: shareUrl,
        onShareFail: () => {},
      })
        .then(() => {
          onClose()
        })
        .catch((error) => {
          logger().error('Share Guild Tickets Failed ', error)
        })
    }
  }, [isMobile, share, emailContent, shareUrl, onClose])

  return (
    <>
      <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-gray-300 p-2" onClick={onClose}>
        <CloseIcon color="black" size={16} />
      </div>
      <FlexColumn className="gap-2 pt-6">
        <SocialSharing
          shareUrl={shareUrl}
          vertical={true}
          title={title}
          copyLinkClass={'pb-32'}
          emailContent={emailContent}
        />
      </FlexColumn>
      <FlexColumn className="grow-1 gap-2 pt-6">
        <FlexColumn className="items-left justify-start gap-2">
          <Button
            onClick={handleGoBack}
            className=" font-color-black items-left w-fit justify-start gap-1.5 rounded-[0.875rem] py-3.5 pl-5 pr-6 text-sm font-medium"
            variant="gray"
          >
            <CaretLeftIcon color={'black'} />
            <LabelMD>{t('back', 'Back')}</LabelMD>
          </Button>
        </FlexColumn>
      </FlexColumn>
    </>
  )
}
